import { Container } from '@/components/shared/container'
import { RichText } from '@/components/shared/rich-text'
import { StoryblokLink } from '@/components/shared/storyblok-link'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { BlokProps } from '@/lib/types'
import { PricingTableStoryblok } from '@/lib/types/component-types-sb'
import { cn } from '@/lib/utils'
import { storyblokEditable } from '@storyblok/react/rsc'
import { CheckIcon, XIcon } from 'lucide-react'
export function PricingTable({ blok }: BlokProps<PricingTableStoryblok>) {
  const itemsLength = blok.items?.length ?? 2

  return (
    <section
      className={'border-border border-y'}
      {...storyblokEditable(blok)}
    >
      <Container
        style={
          {
            '--cols': `repeat(${itemsLength}, minmax(0,1fr))`,
          } as React.CSSProperties
        }
        className={
          'border-border divide-border grid grid-cols-1 divide-y border-x lg:grid-cols-(--cols) lg:divide-x lg:divide-y-0'
        }
      >
        {blok.items?.map((item, i) => {
          const link = item.link?.at(0)
          return (
            <div
              key={item._uid}
              {...storyblokEditable(item)}
            >
              <div className={'border-border space-y-5 border-b px-4 py-7 lg:px-16 lg:py-14'}>
                <div className={'space-y-1'}>
                  <div className={'space-y-2'}>
                    <span className={'block text-sm font-medium'}>{item.preTitle}</span>
                    <div className={'flex items-center gap-2.5'}>
                      <h2 className={'text-accent-foreground text-2xl font-semibold lg:text-4xl'}>
                        {item.title}
                      </h2>
                      <span className={'text-muted-foreground text-lg lg:text-2xl'}>
                        [
                        {item.title
                          ?.split(' ')
                          .map((p) => p.substring(0, 1))
                          .join('')}
                        ]
                      </span>
                    </div>
                  </div>
                  <RichText content={item.description} />
                </div>
                <div className={'flex flex-wrap items-center gap-2.5'}>
                  {item.focusBusinesses?.split(';').map((business) => (
                    <Badge
                      key={business}
                      variant={'secondary'}
                    >
                      {business}
                    </Badge>
                  ))}
                </div>
                <span
                  className={cn({
                    'text-2xl font-semibold': true,
                    'from-brand-lighter to-brand-darker bg-gradient-to-r bg-clip-text text-transparent':
                      item.free,
                  })}
                >
                  {item.price}
                </span>
              </div>
              <div className={'space-y-9 p-8 px-4 lg:p-16'}>
                <div className={'space-y-3.5 text-sm lg:text-base'}>
                  {item.includedFeatures &&
                    item.includedFeatures?.split('\n').map((feature) => (
                      <div
                        key={feature}
                        className={'flex items-start gap-2.5'}
                      >
                        <CheckIcon /> {feature}
                      </div>
                    ))}
                  {item.excludedFeatures &&
                    item.excludedFeatures?.split('\n').map((feature) => (
                      <div
                        key={feature}
                        className={'text-muted-foreground flex items-center gap-2.5'}
                      >
                        <XIcon /> {feature}
                      </div>
                    ))}
                </div>
                {link && (
                  <Button
                    variant={i == itemsLength - 1 ? 'default' : 'secondary'}
                    size={'lg'}
                    className={'w-full'}
                    asChild
                  >
                    <StoryblokLink link={link.target}>{link.label}</StoryblokLink>
                  </Button>
                )}
              </div>
            </div>
          )
        })}
      </Container>
    </section>
  )
}
